<template>
	<div class="c-article-media-video">
		<div v-if="showPoster" class="c-article-media-video__poster">
			<slot></slot>

			<DsButton
				v-if="visibleVideoCtaButtonLabel.length"
				:variant="videoCtaButtonVariant"
				iconStart="button-play"
				class="c-article-media-video__poster-play"
				:aria-label="safeVideoCtaButtonAriaLabel"
				:clickHandler="playVideo"
			>
				{{ visibleVideoCtaButtonLabel }}
			</DsButton>

			<DsIconButton
				v-else
				type="button"
				:aria-label="safeVideoCtaButtonAriaLabel"
				icon-name="button-play"
				:variant="videoCtaButtonVariant"
				size="md"
				class="c-article-media-video__poster-play"
				:clickHandler="playVideo"
			/>
		</div>

		<iframe
			v-else-if="embedUrl"
			ref="videoIframeRef"
			class="c-article-media-video__iframe"
			allow="autoplay; encrypted-media; picture-in-picture; clipboard-write; accelerometer; gyroscope; web-share;"
			allowfullscreen
			referrerpolicy="strict-origin-when-cross-origin"
			loading="lazy"
			tabindex="-1"
			:src="embedUrl"
			:title="safeVideoIframeTitle"
			data-testid="media-video-player"
		>
		</iframe>
	</div>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref } from 'vue'
import { DsButton, DsIconButton } from '@coloplast/design-system'
import { type NormalizedVideo, normalizeVideo } from 'shared/utils/videoEmbed.utils'

const props = defineProps<{
	videoCtaButtonAriaLabel?: string
	videoCtaButtonLabel?: string
	videoIframeTitle?: string
	videoUrl: string
	videoCtaButtonVariant: 'primary' | 'primary-inverted'
}>()

const normalized = ref<NormalizedVideo>(normalizeVideo(props.videoUrl))
const showPoster = ref(true)
const videoIframeRef = ref<HTMLIFrameElement | null>(null)

const embedUrl = computed(() => normalized.value.embedUrl)

const visibleVideoCtaButtonLabel = computed(() => {
	return props.videoCtaButtonLabel?.trim() || ''
})

const safeVideoCtaButtonAriaLabel = computed(() => {
	return props.videoCtaButtonAriaLabel?.trim() || props.videoCtaButtonLabel?.trim() || 'Play video'
})

const safeVideoIframeTitle = computed(() => {
	return props.videoIframeTitle?.trim() || 'Embedded video player'
})

const playVideo = async () => {
	showPoster.value = false

	await nextTick()

	videoIframeRef.value?.focus()
}
</script>
